import React, { useState } from "react";
import StatusPill from "./StatusPill";
import ActionTimeline from "./ActionTimeline";
import AIThoughtBox from "./AIThoughtBox";
import { API_BASE_URL } from "../../config";
import PdfButton from "./PdfButton";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DraftCard from './DraftCard';
import ReviewCard from './ReviewCard';
import AlertListArtifact from './AlertListArtifact';

const AIResponse = ({
  message,
  isLatest = false,
  isLoading = false,
  streamData = [],
  onApplyScreenerFilters = null,
  onOpenPdf,
  onEditProposal = null,
  onDecision = null
}) => {
  const [fullscreenImage, setFullscreenImage] = useState(null);

  if (!message) return null;

  const getMessageContent = (msg) => {
    if (!msg) return "";
    if (msg.parts && msg.parts.length > 0) {
      const text = msg.parts
        .filter((p) => p.type === "text" || p.text !== undefined)
        .map((p) => p.text)
        .join("");
      if (text) return text;
    }
    if (typeof msg.content === "string") return msg.content;
    if (msg.content?.text) return msg.content.text;
    return "";
  };

  const getMessageData = (msg, isLatestMsg) => {
    if (!msg) return [];

    // Extract from parts (new protocol)
    const partsData =
      msg.parts
        ?.map((p) => {
          if (p.type?.startsWith("data-") || p.type === "thought-delta") {
            // Keep object structure if it exists, otherwise use p.data or p
            const item = p.data || p;
            if (typeof item === 'object' && item !== null) {
              return { ...item, sseType: p.type };
            }
            return { value: item, sseType: p.type };
          }
          return null;
        })
        .filter(Boolean) || [];

    // Extract from message.data (legacy or standard data protocol)
    const legacyData = Array.isArray(msg.data)
      ? msg.data
      : msg.data
        ? [msg.data]
        : [];
    const annotations = msg.annotations || [];

    const combined = [...partsData, ...legacyData, ...annotations];

    // Synthesize finish event when message is done
    if (msg.role === "assistant" && !isLatestMsg) {
      if (!combined.some((d) => d.type === "finish")) {
        combined.push({ type: "finish" });
      }
    }
    return combined;
  };

  const rawContent = getMessageContent(message);

  // Parse historical <thought> tags from content (handles literal and encoded variants)
  const thoughtsFromTags = [];
  const thoughtRegex =
    /(?:<thought>|&lt;thought&gt;)([\s\S]*?)(?:<\/thought>|&lt;\/thought&gt;|$)/gi;
  let match;
  while ((match = thoughtRegex.exec(rawContent)) !== null) {
    if (match[1]) thoughtsFromTags.push(match[1].trim());
  }
  const cleanedContent = rawContent.replace(thoughtRegex, "").trim();

  const data = isLatest ? streamData : getMessageData(message, false);

  return (
    <div
      className={`flex gap-4 ${message.role === "user" ? "justify-end" : "justify-start"}`}
    >
      {message.role === "assistant" && (
        <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center flex-shrink-0">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 3L4 14H11L11 21L20 10H13L13 3Z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
      <div
        className={`max-w-[85%] flex flex-col ${message.role === "user" ? "items-end" : "items-start"}`}
      >
        {message.role === "assistant" && (
          <ActionTimeline data={data} isStreaming={isLatest && isLoading} />
        )}

        <div
          className={`rounded-2xl px-5 py-3.5 
                      ${message.role === "user"
              ? "bg-gray-100 text-gray-900 rounded-tr-sm shadow-sm"
              : "bg-white text-gray-900 border border-gray-100 rounded-tl-sm shadow-sm"
            }`}
        >
          {/* Render Thoughts */}
          {(() => {
            const thoughtGroups = data.reduce((acc, item) => {
              if (!item) return acc;

              // Handle explicit thought-delta or custom data-thought
              const isThought =
                item.type === "thought-delta" || item.type === "data-thought";
              const thoughtText = item.delta || item.content || item.text;

              if (isThought && thoughtText) {
                const id = item.id || "default-thought";
                if (!acc[id]) acc[id] = "";
                acc[id] += thoughtText;
              }

              // Handle reflections from think_tool
              if (item.type === "tool_call" && item.name === "think_tool") {
                let reflection = "";

                // Handle both object and stringified args
                if (item.args?.reflection) {
                  reflection = item.args.reflection;
                } else if (typeof item.args === "string") {
                  // Robust extraction: find 'reflection': or "reflection":
                  // and then grab everything between the first following quote and the last quote in the string
                  const keyMatch = item.args.match(/['"]reflection['"]\s*:/);
                  if (keyMatch) {
                    const afterKey = item.args
                      .substring(keyMatch.index + keyMatch[0].length)
                      .trim();
                    const firstQuote = afterKey[0];
                    if (firstQuote === "'" || firstQuote === '"') {
                      const lastQuote = afterKey.lastIndexOf(firstQuote);
                      if (lastQuote > 0) {
                        reflection = afterKey.substring(1, lastQuote);
                        // Unescape common JSON/Python escape sequences
                        reflection = reflection
                          .replace(/\\n/g, "\n")
                          .replace(/\\"/g, '"')
                          .replace(/\\'/g, "'")
                          .replace(/\\\\/g, "\\");
                      }
                    }
                  }
                }

                if (reflection) {
                  // De-duplicate: If this exact content already exists in another thought box, skip it
                  const isDuplicate = Object.values(acc).some(
                    (val) => val.trim() === reflection.trim(),
                  );

                  if (!isDuplicate) {
                    const id =
                      item.id ||
                      `reflection-${Math.random().toString(36).slice(2, 7)}`;
                    acc[id] = reflection;
                  }
                }
              }

              return acc;
            }, {});

            // Inject extracted thoughts from tags if not already present
            thoughtsFromTags.forEach((th, idx) => {
              const isDuplicate = Object.values(thoughtGroups).some(
                (val) => val.trim() === th.trim(),
              );
              if (!isDuplicate) {
                thoughtGroups[`extracted-thought-${idx}`] = th;
              }
            });

            return Object.entries(thoughtGroups).map(([id, text]) => (
              <AIThoughtBox
                key={id}
                content={text}
                isStreaming={
                  isLatest &&
                  isLoading &&
                  (data[data.length - 1]?.id === id ||
                    (data[data.length - 1]?.type?.includes("thought") &&
                      id.includes("thought")))
                }
              />
            ));
          })()}

          {(() => {
            if (cleanedContent && message.role === 'user' && (cleanedContent.startsWith('{') || cleanedContent.startsWith('['))) {
              try {
                const parsed = JSON.parse(cleanedContent);
                if (parsed.type === 'resume' && parsed.decision) {
                  const decisionType = parsed.decision.type;
                  return (
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm border ${decisionType === 'approve'
                      ? 'bg-green-50 text-green-700 border-green-100'
                      : 'bg-gray-50 text-gray-500 border-gray-100'
                      }`}>
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white shadow-sm border border-inherit">
                        {decisionType === 'approve' ? '✅' : '❌'}
                      </span>
                      {decisionType === 'approve' ? 'Action Approved' : 'Action Rejected'}
                    </div>
                  );
                }
              } catch (e) {
                // Not a valid JSON decision message, fall back to markdown
              }
            }

            // Heuristic Alert List Parsing
            // Detects patterns like: - **Alert #10**: Name \n - Category: ... \n - Status: ... \n - Criteria: ...
            const alertRegex = /- \*\*Alert #(\d+)\*\*: (.*?)\n\s*- Category: (.*?)\n\s*- Status: (.*?)\n\s*- Criteria: (.*?)(?=\n- \*\*Alert|$)/gs;
            const detectedAlerts = [];
            let alertMatch;
            while ((alertMatch = alertRegex.exec(cleanedContent)) !== null) {
              detectedAlerts.push({
                id: alertMatch[1],
                name: alertMatch[2].trim(),
                category: alertMatch[3].trim(),
                status: alertMatch[4].trim(),
                criteria: alertMatch[5].trim()
              });
            }

            if (detectedAlerts.length > 0) {
              const displayContent = cleanedContent.replace(alertRegex, '').trim();
              return (
                <div className="flex flex-col gap-4">
                  <AlertListArtifact alerts={detectedAlerts} />
                  {displayContent && (
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {displayContent}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              );
            }

            return cleanedContent && (
              <div className="prose prose-sm max-w-none">
                {(() => {
                  // If we have PDF artifacts, try to replace their titles in the text with buttons
                  // New Logic: Replace [Source: Title - Page No, r2_url: url]
                  // Parse direct citation with URL from text, no artifact lookup needed

                  // New Logic: Replace [SOURCE, PDF Title, Page No, r2_url]
                  // Regex breakdown:
                  // (?:\*\*)?     -> optional starting bold marker "**"
                  // \[SOURCE,\s*  -> matches start literal "[SOURCE,"
                  // ([^,]+)       -> Capture Group 1: Title (everything until next comma)
                  // ,\s*([^,]+)   -> Capture Group 2: Page No (everything until next comma)
                  // ,\s*([^\]]+)  -> Capture Group 3: r2_url (everything until closing bracket)
                  // \]            -> matches end literal "]"
                  // (?:\*\*)?     -> optional ending bold marker "**"
                  const sourceRegex =
                    /(?:\*\*)?\[SOURCE,\s*([^,]+),\s*([^,]+),\s*([^\]]+)\](?:\*\*)?/g;

                  if (sourceRegex.test(cleanedContent)) {
                    const parts = [];
                    let lastIndex = 0;
                    let match;

                    sourceRegex.lastIndex = 0;

                    while ((match = sourceRegex.exec(cleanedContent)) !== null) {
                      if (match.index > lastIndex) {
                        parts.push(
                          <ReactMarkdown
                            key={`text-${lastIndex}`}
                            remarkPlugins={[remarkGfm]}
                            components={{
                              p: ({ node, ...props }) => (
                                <p
                                  className="whitespace-pre-wrap leading-relaxed transition-all duration-300 mb-2"
                                  {...props}
                                />
                              ),
                            }}
                          >
                            {cleanedContent.substring(lastIndex, match.index)}
                          </ReactMarkdown>,
                        );
                      }

                      const title = match[1].trim();
                      const pageArg = match[2].trim();
                      // Extract just the number if "Page" word is present, otherwise split/parse
                      const pageMatch = pageArg.match(/(\d+)/);
                      const page = pageMatch ? pageMatch[0] : pageArg;

                      const r2Url = match[3].trim();

                      console.log("r2Url", r2Url);
                      console.log("page", page);
                      console.log("title", title);

                      parts.push(
                        <PdfButton
                          key={`source-${match.index}`}
                          onClick={() => {
                            console.log("r2Url", r2Url);
                            console.log("page", page);
                            console.log("title", title);
                            console.log("onOpenPdf", onOpenPdf);
                            if (onOpenPdf) onOpenPdf(r2Url, page, title);
                            else console.error("onOpenPdf prop missing");
                          }}
                        >
                          {title}, Page {page}
                        </PdfButton>,
                      );

                      lastIndex = sourceRegex.lastIndex;
                    }

                    if (lastIndex < cleanedContent.length) {
                      parts.push(
                        <ReactMarkdown
                          key={`text-${lastIndex}`}
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ node, ...props }) => (
                              <p
                                className="whitespace-pre-wrap leading-relaxed transition-all duration-300 mb-2"
                                {...props}
                              />
                            ),
                          }}
                        >
                          {cleanedContent.substring(lastIndex)}
                        </ReactMarkdown>,
                      );
                    }
                    return parts;
                  }

                  return (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ node, ...props }) => (
                          <p
                            className="whitespace-pre-wrap leading-relaxed transition-all duration-300 mb-2"
                            {...props}
                          />
                        ),
                      }}
                    >
                      {cleanedContent}
                    </ReactMarkdown>
                  );
                })()}
              </div>
            );
          })()}

          {/* Render User Attachments (Screenshots/Uploads) */}
          {(message.experimental_attachments || message.attachments)?.map(
            (attachment, i) => (
              <div key={`user-att-${i}`} className="mt-2 group relative">
                <img
                  src={attachment.url}
                  alt={attachment.name || "User content"}
                  className="rounded-lg max-w-full shadow-sm cursor-zoom-in hover:opacity-95 transition-all active:scale-[0.98]"
                  onClick={() => setFullscreenImage(attachment.url)}
                />
              </div>
            ),
          )}

          {/* Render AI-generated images from message data */}
          {message.data?.images &&
            message.data.images.map((img, i) => {
              const imgSrc = img.startsWith("data:")
                ? img
                : `${API_BASE_URL}${img}`;
              return (
                <div key={`ai-img-${i}`} className="mt-2 group relative">
                  <img
                    src={imgSrc}
                    alt="AI Content"
                    className="rounded-lg max-w-full shadow-sm cursor-zoom-in hover:opacity-95 transition-all active:scale-[0.98]"
                    onClick={() => setFullscreenImage(imgSrc)}
                    onError={(e) => (e.target.style.display = "none")}
                  />
                </div>
              );
            })}

          {/* Render chart/image artifacts from the live stream (merged from main_v2) */}
          {data
            .filter((d) => {
              if (!d || typeof d !== "object") return false;
              const type = d.type;
              const path = d.data?.path ?? d.path;
              return (
                path &&
                (type === "technical_chart" ||
                  type === "image" ||
                  (typeof type === "string" &&
                    (type.includes("chart") || type.includes("image"))))
              );
            })
            .map((art, idx) => {
              const path = art.data?.path ?? art.path;
              const src = path.startsWith("http")
                ? path
                : `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
              return (
                <div
                  key={`artifact-img-${idx}`}
                  className="mt-2 group relative"
                >
                  <img
                    src={src}
                    alt={
                      art.data?.symbol ? `Chart ${art.data.symbol}` : "Chart"
                    }
                    className="rounded-lg max-w-full shadow-sm border border-gray-100 cursor-zoom-in hover:opacity-95 transition-all active:scale-[0.98]"
                    onClick={() => setFullscreenImage(src)}
                    onError={(e) => (e.target.style.display = "none")}
                  />
                </div>
              );
            })}

          {/* Render Screener Artifacts */}
          {data
            .filter((d) => d.type === "screener_table")
            .map((art, idx) => (
              <div
                key={idx}
                className="mt-4 p-4 bg-blue-50/50 rounded-xl border border-blue-100 flex flex-col gap-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-blue-900 flex items-center gap-2">
                      ✨ {art.total_count || 0} stocks found
                    </span>
                    <span className="text-[10px] text-blue-600 font-medium">
                      Agent-driven screening criteria
                    </span>
                  </div>
                  {onApplyScreenerFilters && art.filter_params?.filters && (
                    <button
                      onClick={() =>
                        onApplyScreenerFilters(art.filter_params.filters)
                      }
                      className="px-4 py-2 bg-blue-600 text-white text-[11px] font-bold rounded-full hover:bg-blue-700 transition-all shadow-sm active:scale-95 flex items-center gap-1.5"
                    >
                      Apply to Table
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>
                  )}
                </div>

                {/* Summary of filters applied */}
                <div className="flex flex-wrap gap-1.5">
                  {art.filter_params?.filters?.map((f, fi) => (
                    <div
                      key={fi}
                      className="px-2 py-0.5 bg-white/80 border border-blue-100 rounded text-[10px] text-blue-800"
                    >
                      <span className="font-semibold">{f.key}</span>: {f.op}{" "}
                      {Array.isArray(f.value) ? f.value.join("-") : f.value}
                    </div>
                  ))}
                </div>
              </div>
            ))}

          {/* Render Draft Proposal Artifacts */}
          {data
            .filter((d) => d.type?.includes("_draft"))
            .map((art, idx) => (
              <DraftCard
                key={idx}
                artifact={art}
                onEditRequest={onEditProposal}
              />
            ))}

          {/* Render HITL Review Cards (New) */}
          {data.filter(d => d.sseType === 'data-interrupt' || d.type === 'data-interrupt').map((art, idx) => (
            <ReviewCard
              key={idx}
              interrupt={art}
              onDecision={onDecision}
              onEditRequest={onEditProposal}
            />
          ))}

          {/* Render Structured Alert List Artifacts */}
          {data
            .filter(d => d.type === 'alerts_list' || d.sseType === 'data-alerts_list')
            .map((art, idx) => (
              <AlertListArtifact
                key={idx}
                alerts={art.alerts || art.value || []}
              />
            ))}

          {/* Render PDF Artifacts */}
          {data
            .filter(
              (d) =>
                (d.type === "data-event" &&
                  d.data?.subagent === "pdf-agent" &&
                  d.data?.artifact?.type === "pdf") ||
                d.type === "pdf",
            )
            .map((event, idx) => {
              const artifactData = event.data?.artifact?.data;
              let pdfs = [];
              if (artifactData) {
                if (Array.isArray(artifactData.sources)) {
                  pdfs = artifactData.sources;
                } else if (Array.isArray(artifactData)) {
                  pdfs = artifactData;
                }
              }

              if (pdfs.length === 0) return null;

              return (
                <div
                  key={`pdf-event-${idx}`}
                  className="mt-4 p-4 bg-red-50/50 rounded-xl border border-red-100 flex flex-col gap-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-red-900 flex items-center gap-2">
                      📄 Found {pdfs.length} Relevant Documents
                    </span>
                  </div>

                  <div className="flex flex-col gap-2">
                    {pdfs.map((pdf, pIdx) => (
                      <button
                        key={pIdx}
                        onClick={() =>
                          onOpenPdf &&
                          onOpenPdf(pdf.r2_url, pdf.page, pdf.title)
                        }
                        className="flex flex-col text-left p-3 bg-white rounded-lg border border-red-100 hover:border-red-300 hover:shadow-md transition-all group"
                      >
                        <div className="flex justify-between items-start w-full">
                          <span className="text-xs font-bold text-gray-800 line-clamp-1 group-hover:text-red-600">
                            {pdf.title}
                          </span>
                          {pdf.relevance_score && (
                            <span className="text-[10px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded ml-2 whitespace-nowrap">
                              {(pdf.relevance_score * 100).toFixed(0)}% match
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500">
                          <span className="font-medium bg-red-50 text-red-600 px-1.5 rounded">
                            {pdf.company}
                          </span>
                          <span>
                            {pdf.year}{" "}
                            {pdf.quarter ? pdf.quarter.toUpperCase() : ""}
                          </span>
                          <span>Page {pdf.page}</span>
                        </div>

                        {pdf.text_preview && (
                          <p className="mt-2 text-[11px] text-gray-400 line-clamp-2 italic border-l-2 border-gray-100 pl-2">
                            "...{pdf.text_preview}..."
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
        </div>

        {isLatest && isLoading && message.role === "assistant" && (
          <div className="mt-3">
            <StatusPill data={data} isLoading={isLoading} />
          </div>
        )}
      </div>

      {/* Premium Fullscreen Lightbox */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md animate-in fade-in duration-300 p-4 sm:p-8"
          onClick={() => setFullscreenImage(null)}
        >
          <button
            className="absolute top-6 right-6 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-[110]"
            onClick={(e) => {
              e.stopPropagation();
              setFullscreenImage(null);
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <div
            className="relative max-w-full max-h-full flex items-center justify-center animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={fullscreenImage}
              alt="Fullscreen view"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};
export default AIResponse;
